import { ReactNode, useEffect, useState } from "react";
import { EditSectionModal } from "../EditSectionModal/EditSectionModal";
import styles from "./EditOverviewModal.module.scss";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Textarea } from "@/frontend/shared/ui/Textarea/Textarea";
import { postGenerateRevisionData } from "../../model/services/postGenerateRevisionData";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { textPattern } from "@/frontend/shared/const/regexPatterns";

type TEditOverviewModal = {
  isOpen: boolean;
  onClose: () => void;
  aiProvidedDescription: string;
  triggerRefetch: () => void;
};

export function EditOverviewModal({ isOpen, onClose, aiProvidedDescription, triggerRefetch }: TEditOverviewModal) {
  const [value, setValue] = useState<string>(aiProvidedDescription || "");
  useEffect(() => {
    setValue(aiProvidedDescription);
  }, [aiProvidedDescription]);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  async function onSave() {
    if (value.length > 5000) {
      snackbarConstructor("Description must be at most than 5000 characters", "error");
      return;
    }
    if (!!value && !textPattern.test(value)) {
      snackbarConstructor(
        "Description can only contain latin letters, spaces, numbers and standart punctuation marks.",
        "error"
      );
      return;
    }

    setIsLoading(true);
    try {
      await postGenerateRevisionData({
        data: { description: value }
      });
      triggerRefetch();
      snackbarConstructor("Overview updated successfully", "normal");
    } catch (e) {
      reportErrorToSentry(e as AxiosError, "EDIT_OVERVIEW_MODAL: /founder-revisions/create-new-revision");
      snackbarConstructor("Error updating data", "error");
    } finally {
      onClose();
      setIsLoading(false);
    }
  }

  function onCloseModal() {
    onClose();
    setIsOpenConfirmationModal(false);
    setValue(aiProvidedDescription || "");
  }

  function onDiscardChanges() {
    if (aiProvidedDescription === value) {
      onClose();
    } else {
      setIsOpenConfirmationModal(true);
    }
  }

  return (
    <EditSectionModal
      isOpen={isOpen}
      onClose={onDiscardChanges}
      title={"Edit Overview"}
      onSave={onSave}
      isLoading={isLoading}
    >
      <div className={styles.container}>
        <Typography variant="label-4" color="greyscale700" className={styles.label}>
          Description
        </Typography>
        <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={9} />
      </div>
      <ConfirmationModal
        isOpen={isOpenConfirmationModal}
        onClose={() => setIsOpenConfirmationModal(false)}
        onConfirm={onCloseModal}
        content={"Are you sure you want to discard the changes you made?"}
        title={"Discard Changes"}
        confirmButtonText={"Discard"}
        cancelButtonText={"No Thanks"}
      />
    </EditSectionModal>
  );
}
